import React, {useEffect, useRef, useState} from "react";
import {isSearchPage, useProductListContext} from "../productListProvider/ProductListProvider";
import {Trans, useTranslation} from "react-i18next";
import ListItemCard, {scrollInfoStorage} from "./ListItemCard";
import {getShopId} from "../../lib/getShopId";
import PatternInterruption from "./PatternInterruption";
import {Tracking} from "../../../tracking/tracking";
import {IItemContent, ProductListFilterItemValueType} from "@connox/typescript-library";
import isSameObject from "../../../helpers/isSameObject";
import LoadingSpinner from "../../atoms/LoadingSpinner";

/**
 * The component showing up the product tiles. If no items are
 * present and still loading, a loading spinner is shown.<br/>
 * If not loading there will be a note saying 'No articles present'
 * @constructor
 */
const ListItems: React.FC = () => {
  const {state} = useProductListContext();
  const {t} = useTranslation();

  const ProductListLoader: React.FC = () => {
    const [productListLoading, setProductListLoading] = useState(false);

    useEffect(() => {
      if (state.loading) {
        setProductListLoading(true);
      } else {
        setProductListLoading(false);
      }
    }, []);

    if (productListLoading) {
      return (
        <div className="product-list__loader py-11">
          <div className="product-list__loader--sticky">
            <LoadingSpinner />
          </div>
        </div>
      );
    } else {
      return null;
    }
  };

  if (!state.loading && !state.items.length) {
    if (isSearchPage) {
      const query = new URLSearchParams(window.location.search).get("q");
      if (query) {
        return (
          <div className="suspenses-container center my-5">
            <Trans
              i18nKey="search.noResultsGiven--query"
              ns="translations"
              components={{
                s: <strong />,
              }}
              values={{query}}
            />
          </div>
        );
      } else {
        return (
          <React.Fragment>
            <div key={0} className="suspenses-container center my-5">
              <div className="h3">{t("search.tryAgain")}</div>
            </div>
            <div key={1} className="suspenses-container center my-5">
              <p>{t("search.pleaseClickMagnifyingGlass")}</p>
            </div>
          </React.Fragment>
        );
      }
    } else {
      return <div className="suspenses-container center my-5">{t("product.noMatchingItems")}</div>;
    }
  }

  return (
    <div className="product-list grid pb-3">
      <ProductListLoader />
      <ItemCards />
    </div>
  );
};

export default ListItems;

const currentHeadline = document.body.querySelector<HTMLElement>(".product-list-react, .product-list-react-redesign")?.dataset.headline;

const ItemCards: React.FC = () => {
  const {state} = useProductListContext();
  const oldListRef = useRef<number[]>([]);
  const oldFiltersRef = useRef<{[p: string]: ProductListFilterItemValueType}>({});

  const scrollInfo = scrollInfoStorage.read(document.body.id);

  const isItemClicked = useRef(false);

  useEffect(() => {
    const saveScrollPosition = () => {
      if (!isItemClicked.current) {
        scrollInfoStorage.write(document.body.id, {itemId: null, position: window.scrollY});
      }
    };
    window.addEventListener("beforeunload", saveScrollPosition);

    if (scrollInfo && scrollInfo.itemId === null && isItemClicked.current) {
      const {position} = scrollInfo;
      if (typeof window.scrollTo === "function") {
        window.scrollTo({left: 0, top: position});
        isItemClicked.current = false;
      } else {
        window.scroll(0, position);
      }
      scrollInfoStorage.remove(document.body.id);
    }
  }, []);

  useEffect(() => {
    const isSameFilterSet = isSameObject<{[p: string]: ProductListFilterItemValueType}>(
      state.filters,
      oldFiltersRef.current,
    );
    const diffItems: {index: number; item: IItemContent}[] = [];
    state.items.forEach((itm, index) => {
      // if state.items has new item on particular index, or it's the same by coincidence from a new filter setting
      if (oldListRef.current[index] !== itm.itemId || !isSameFilterSet) {
        diffItems.push({item: itm, index});
      }
    });

    Tracking.trackProductImpressions(diffItems, currentHeadline);

    oldListRef.current = state.items.map((itm) => itm.itemId);
    oldFiltersRef.current = state.filters;
  }, [state.items]);

  const elementsArray = state.items.map((item, index) => {
    const onClick = () => {
      scrollInfoStorage.write(document.body.id, {itemId: item.itemId, position: window.scrollY});
      isItemClicked.current = true;

      Tracking.trackImpressionClicked(
        item,
        index,
        document.body.querySelector<HTMLElement>(".product-list-react, .product-list-react-redesign")?.dataset.headline,
      );
    };

    return (
      <ListItemCard
        key={index}
        item={item}
        onClick={onClick}
        swipeable={false}
        scrollInfo={scrollInfo}
        lazyLoading={index > 5}
        customClass={`column-4 column-m-2 ${state.showFilters ? "column-l-4" : "column-l-3"}`}
      />
    );
  });
  if (getShopId() === 100001 && state.items.length >= 12 && document.body.id !== "C100799") {
    elementsArray.splice(
      12,
      0,
      <PatternInterruption key="pattern-interruption" customClass={"column-8 column-l-12"} />,
    );
  }

  return <>{elementsArray}</>;
};
